import {
  useEffect,
  useState,
} from 'react';

import { datadogRum } from '@datadog/browser-rum';
import { useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';

import {
  trackApplyEvent,
  uiRoutes,
  useOptimizelyFeature,
  useSendEmailCode,
} from '@consumer-frontend/nc-api';
import {
  initialAlertBannerProps,
  isDefined,
  redirectTo,
  somethingWentWrongAlertBannerProps,
} from '@consumer-frontend/nc-utils';
import { Container, Fieldset } from '@consumer-frontend/shared-ui';

import { useApplyRouting } from '../../routing';
import { alertBannerAtom } from '../../store';
import { NcButton } from '../NcButton';
import { NcPageWithAlertBanner } from '../NcPageWithAlertBanner';

import '@consumer-bootstrap/netcredit.scss';
// eslint-disable-next-line import/order
import '@consumer-bootstrap/brands/netcredit/apply/verify_email.scss';

export const NcVerifyEmailPage: React.FC = () => {
  const isSigninFeatureEnabled = useOptimizelyFeature('app_redesign_m3_experiment_sign_in_with_password_routing');
  const setAlertBanner = useSetAtom(alertBannerAtom);

  const navigate = useNavigate();
  const { handleContinue } = useApplyRouting();
  const {
    error,
    isMutating,
    sendEmailCode,
  } = useSendEmailCode();

  const [isRedirecting, setIsRedirecting] = useState(false);

  // Sets status and alert when sendEmailCode has an error
  useEffect(() => {
    if (error?.response?.status === 422) {
      navigate(uiRoutes.apply.borrowingNeeds);
    } else if (error) {
      setAlertBanner(somethingWentWrongAlertBannerProps);
    }
  }, [
    error,
    navigate,
    setAlertBanner,
  ]);

  const handleSendEmailCode = async (): Promise<void> => {
    datadogRum.addAction('verifyEmailOptionSelected', { option: 'otp' });
    trackApplyEvent('sendCodeViaEmailClicked');
    setAlertBanner(initialAlertBannerProps);

    const response = await sendEmailCode();

    if (!isDefined(response)) return;

    await handleContinue({});
  };

  const handleSignInWithPassword = (): void => {
    const redirectMetadata = isSigninFeatureEnabled ? 'apply_sign_in' : 'sign_in';

    datadogRum.addAction('verifyEmailOptionSelected', { option: 'password', redirect: redirectMetadata });
    trackApplyEvent('signInWithPasswordClicked', { redirect: redirectMetadata });

    if (!isSigninFeatureEnabled) {
      setIsRedirecting(true);
      redirectTo(uiRoutes.legacyAH.signin);

      return;
    }

    navigate(uiRoutes.apply.signin);
  };

  return (
    <NcPageWithAlertBanner>
      <Container className="nc-verify-email-page" hasBoxShadow>

        <h1 className="h3">You may have applied with us before.</h1>

        <p>Verify your email to continue.</p>

        <Fieldset className="cta-buttons">
          <NcButton
            loading={isMutating || isRedirecting}
            onClick={handleSendEmailCode}
            type="button"
            variant="outline-primary"
          >
            Send Code via Email
          </NcButton>

          <NcButton
            disabled={isMutating || isRedirecting}
            onClick={handleSignInWithPassword}
            type="button"
            variant="outline-primary"
          >
            Sign In with Password
          </NcButton>
        </Fieldset>
      </Container>
    </NcPageWithAlertBanner>
  );
};
